import { SdkContext } from '../internal'

/** One secret of the community vault: never its value, only the last 4 characters. */
export interface VaultSecret {
  name: string
  masked: string
  last4: string
  allowedHosts: string[]
  createdAt?: string
  updatedAt?: string
  lastUsedAt: string | null
}

/** Where the community's HTTP calls leave from: the fixed IPs (Business and up), within a monthly cap. */
export interface VaultOutbound {
  fixedIp: boolean
  ips: string[]
  monthlyCap: number | null
  usedThisMonth: number | null
  month?: string
}

export interface VaultDataNotice {
  version: string
  accepted: boolean
  acceptedAt: string | null
  acceptedById: string | null
  /** The notice in the reader's language (GET /vault/data-notice). */
  text?: string | null
}

/** F-144 B: the token ceilings of the AI blocks (`/vault/ai-limits`, owner and admin). */
export interface VaultAiLimits {
  maxTokensPerCall: number
  maxTokensPerRun: number
  maxTokensPerDay: number
  custom: boolean
  defaults: { maxTokensPerCall: number; maxTokensPerRun: number; maxTokensPerDay: number }
  usedToday: number
}

/** F-206: the signing secret of "Chamar webhook" (`/vault/signing-secret`); the value never comes back after the `POST`. */
export interface VaultSigningSecret {
  configured: boolean
  masked?: string | null
  last4?: string | null
  configuredAt?: string | null
}

/** The `POST` answer: the only time the value (`secret`) is sent. */
export interface VaultSigningSecretGenerated extends VaultSigningSecret {
  secret: string
  rotated?: boolean
}

/** F-144: the community vault (`/vault`, owner and admin). Values are write-only. */
export class VaultResource {
  constructor(private ctx: SdkContext) {}

  async list(): Promise<{ secrets: VaultSecret[]; dataNotice: VaultDataNotice; outbound?: VaultOutbound | null }> {
    return this.ctx.call({ httpMethod: 'GET', path: '/vault' })
  }

  /** Creates or rotates a secret; `value` left out keeps the current value (changes only the hosts). */
  async put(name: string, body: { value?: string; allowedHosts?: string[] }): Promise<VaultSecret> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/vault/secrets/{name}', hasBody: true }, { name, body })
  }

  async remove(name: string): Promise<unknown> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/vault/secrets/{name}' }, { name })
  }

  async dataNotice(): Promise<VaultDataNotice> {
    return this.ctx.call({ httpMethod: 'GET', path: '/vault/data-notice' })
  }

  async acceptDataNotice(version: string): Promise<VaultDataNotice> {
    return this.ctx.call({ httpMethod: 'POST', path: '/vault/data-notice', hasBody: true }, { body: { accept: true, version } })
  }

  /** F-206 (owner and admin): whether the community signs its webhook calls, without the value. */
  async signingSecret(): Promise<VaultSigningSecret> {
    return this.ctx.call({ httpMethod: 'GET', path: '/vault/signing-secret' })
  }

  /** F-206: generates it, or with `rotate` replaces it (409 `vault.signingSecretExists` without it); the value comes only here. */
  async generateSigningSecret(rotate = false): Promise<VaultSigningSecretGenerated> {
    return this.ctx.call({ httpMethod: 'POST', path: '/vault/signing-secret', hasBody: true }, { body: rotate ? { rotate: true } : {} })
  }

  /** F-206: the calls go out unsigned again. */
  async removeSigningSecret(): Promise<unknown> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/vault/signing-secret' })
  }

  async aiLimits(): Promise<VaultAiLimits> {
    return this.ctx.call({ httpMethod: 'GET', path: '/vault/ai-limits' })
  }

  async putAiLimits(body: Partial<Pick<VaultAiLimits, 'maxTokensPerCall' | 'maxTokensPerRun' | 'maxTokensPerDay'>>): Promise<VaultAiLimits> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/vault/ai-limits', hasBody: true }, { body })
  }
}
